/**
 * DecisionFlow — BulkAssignmentWizard
 * ui/src/components/admin/BulkAssignmentWizard.jsx
 * S547 — §6 Six-Stage Bulk Assignment Wizard
 *
 * Architecture: governed pre-flight before a single all-or-nothing POST.
 * POST /api/v1/plan-assignments/bulk-import has no partial-accept path —
 * any single row failure = 422, nothing written. The wizard ensures
 * zero-failure entry to the commit stage.
 *
 * Stage flow:
 *   1 — Choose Plan        client-side filter, approved plans only
 *   2 — Choose Population  filter criteria → population summary
 *   3 — Analyze Population GET /api/v1/plan-assignments/analyze?fiscal_period_id={id}&plan_id={id}
 *   4 — Resolve Conflicts  GET /api/v1/plan-assignments/conflicts?fiscal_period_id={id}&plan_id={id}
 *   5 — Preview Assignment POST /api/v1/plan-assignments/bulk-import?dry_run=true
 *   6 — Commit             POST /api/v1/plan-assignments/bulk-import?dry_run=false
 *
 * Field contract — §E5 (UnifiedImportSpec §3.5) — LOCKED:
 *   Customer-visible: employee_id, plan_code, fiscal_period_code,
 *                     business_unit_code, effective_date
 *   Server resolves:  position_id, plan_id, fiscal_period_id,
 *                     business_unit_id, plan_component_id, role_id, segment_id
 *   NEVER show any integer FK or UUID on any user-facing surface.
 *
 * Error code → operational language map (§6 Conflict Display Language rule):
 *   Never surface raw error codes. All codes translated before display.
 *
 * Controls (non-negotiable):
 *   - NN-03: No AI in any stage — all detection is deterministic
 *   - NN-36: Two-actor approval — self-approval blocked at API level
 *   - surface='comp_policies' on audit writes (UI-05)
 *   - Error envelope: resp.json()["detail"]["error"]["code"]
 *   - All tables: max-h-* + overflow-y-auto + sticky thead
 *   - CSS tokens only — rgb(var(--color-*)) — never hardcoded hex
 *   - Do NOT modify PlanAssignmentManager.jsx
 *
 * Spec authority: comp_policies_spec_s539.md §6 + UnifiedImportSpec_v1.md §3.5
 * DDL baseline: ddl_summary_r49.md (plan_assignment verified)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle,
  XCircle, ShieldCheck, ShieldAlert, Clock, Info, RefreshCw,
  Users, ClipboardList, Search, AlertCircle, CheckCircle,
  FileText, Layers3, ExternalLink,
} from "lucide-react";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// §6 Conflict Display Language — error code → operational UI string
// NEVER surface raw error codes. NEVER show FK violation language.
// ---------------------------------------------------------------------------
const ERROR_MESSAGES = {
  IMPORT_FK_VIOLATION:       "Position / plan / fiscal period / business unit not found or inactive",
  PERIOD_NOT_OPEN:           "Fiscal period is not open for assignments",
  PLAN_NOT_APPROVED:         "Plan must be approved before assignment",
  PLAN_CODE_RESOLUTION_FAILED: "No active plan component found for this role and segment",
  DUPLICATE_ASSIGNMENT:      "An active assignment already exists — supersede existing before reassigning",
};

function resolveErrorMessage(code, fallback) {
  return ERROR_MESSAGES[code] ?? fallback ?? "An error occurred. Please try again.";
}

// ---------------------------------------------------------------------------
// §6 Stage 4 — 15 conflict categories, labels, recommended resolutions
// Category 11 (amendment_in_progress) has no inline resolution — link only.
// ---------------------------------------------------------------------------
const CONFLICT_CATEGORIES = [
  { key: "active_plan_exists",        label: "Active Plan Exists",          options: ["Replace Existing", "Schedule Future", "Exclude"] },
  { key: "effective_date_overlap",    label: "Effective Date Overlap",       options: ["Replace Existing", "Exclude", "Manual Review"] },
  { key: "position_not_active",       label: "Position Not Active",          options: ["Exclude", "Schedule Future"] },
  { key: "ramp_schedule_conflict",    label: "Ramp Schedule Conflict",       options: ["Replace Existing", "Exclude", "Manual Review"] },
  { key: "multiple_business_units",   label: "Multiple Business Units",      options: ["Exclude", "Manual Review"] },
  { key: "pending_plan_approval",     label: "Pending Plan Approval",        options: ["Exclude"] },
  { key: "pending_acknowledgment",    label: "Pending Acknowledgment",       options: ["Exclude", "Manual Review"] },
  { key: "missing_eligibility",       label: "Missing Eligibility",          options: ["Exclude", "Manual Review"] },
  { key: "duplicate_assignment",      label: "Duplicate Assignment",         options: ["Replace Existing", "Exclude"] },
  { key: "future_assignment_exists",  label: "Future Assignment Exists",     options: ["Replace Existing", "Exclude", "Manual Review"] },
  { key: "amendment_in_progress",     label: "Plan Amendment In Progress",   options: [] }, // link only — no inline resolution
  { key: "payroll_freeze_window",     label: "Payroll Freeze Window",        options: ["Exclude"] },
  { key: "position_on_hold",          label: "Position On Hold",             options: ["Exclude", "Manual Review"] },
  { key: "readiness_failure",         label: "Readiness Failure",            options: ["Exclude", "Manual Review"] },
  { key: "calculation_run_conflict",  label: "Calculation Run Conflict",     options: ["Exclude"] },
];

// ---------------------------------------------------------------------------
// Internal toast — self-contained, 3000ms auto-dismiss (mastercontext §UI)
// ---------------------------------------------------------------------------
function WizardToast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-surface))] px-5 py-3 shadow-lg">
        <CheckCircle className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{message}</p>
        <button onClick={onDismiss} className="ml-2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitives — match CompensationPoliciesWorkspace exactly
// ---------------------------------------------------------------------------
function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// §6 "All rows must be valid" — persistent notice, not dismissible
function AllOrNothingNotice() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
      <p className="text-tok-sm text-[rgb(var(--color-warning))]">
        <span className="font-semibold">All rows must be valid.</span>{" "}
        Any error blocks the entire batch — nothing is written until all rows pass validation.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage progress indicator
// ---------------------------------------------------------------------------
const STAGE_LABELS = [
  "Choose Plan",
  "Choose Population",
  "Analyze Population",
  "Resolve Conflicts",
  "Preview Assignment",
  "Commit",
];

function StageProgress({ current }) {
  return (
    <div className="flex items-center gap-0">
      {STAGE_LABELS.map((label, idx) => {
        const stageNum  = idx + 1;
        const isDone    = stageNum < current;
        const isActive  = stageNum === current;
        const isPending = stageNum > current;

        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-tok-xs font-semibold transition-colors ${
                isDone
                  ? "border-[rgb(var(--color-success))] bg-[rgb(var(--color-success))] text-white"
                  : isActive
                    ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white"
                    : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
              }`}>
                {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : stageNum}
              </div>
              <span className={`mt-1 hidden text-center text-[10px] leading-tight sm:block ${
                isActive ? "font-semibold text-[rgb(var(--color-text))]" : "text-[rgb(var(--color-text-muted))]"
              }`} style={{ maxWidth: "60px" }}>
                {label}
              </span>
            </div>
            {idx < STAGE_LABELS.length - 1 && (
              <div className={`mx-1 h-0.5 w-8 sm:w-14 ${
                isDone ? "bg-[rgb(var(--color-success))]" : "bg-[rgb(var(--color-border))]"
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 1 — Choose Plan
// Fetch all plans, filter client-side to approved only.
// Display: name, code, version, role, segment, component count.
// Never show plan_id or any integer FK.
// ---------------------------------------------------------------------------
function Stage1ChoosePlan({ selected, onSelect }) {
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/plans`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        // Filter to approved only — draft/pending never shown (§6 requirement)
        const all      = data?.plans ?? data?.data ?? [];
        const approved = all.filter((p) => p.plan_status === "approved");
        setPlans(approved);
      })
      .catch((e) => setError(e.message ?? "Unable to load plans."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? plans.filter((p) =>
        (p.plan_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (p.plan_code ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : plans;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Choose Plan</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Only approved plans are available for bulk assignment. Draft and pending plans are not shown.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or code…"
          className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-2 pl-9 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
        />
      </div>

      {loading && <LoadingRow />}
      {error   && <ErrorNote message={error} />}

      {!loading && !error && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-[rgb(var(--color-border))] px-4 py-8 text-center">
          <ClipboardList className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
            {search ? "No approved plans match your search." : "No approved plans available."}
          </p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Plans must be approved before they can be assigned.
          </p>
        </div>
      )}

      {/* Plan list — scroll-bounded per mastercontext */}
      {!loading && !error && filtered.length > 0 && (
        <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
          {filtered.map((plan) => {
            const isSelected = selected?.plan_code === plan.plan_code;
            return (
              <button
                key={plan.plan_code}
                onClick={() => onSelect(plan)}
                aria-selected={isSelected}
                className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                  isSelected
                    ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                    : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-tok-sm text-[rgb(var(--color-text))] truncate">
                        {plan.plan_name ?? plan.plan_code}
                      </p>
                      {/* Approved badge */}
                      <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-[10px] font-semibold text-[rgb(var(--color-success))]">
                        <CheckCircle2 className="h-3 w-3" />
                        Approved
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                      {/* Human-readable fields only — never plan_id */}
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                        Code: <span className="font-mono font-medium text-[rgb(var(--color-text))]">{plan.plan_code}</span>
                      </span>
                      {plan.version != null && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          Version: <span className="font-medium text-[rgb(var(--color-text))]">v{plan.version}</span>
                        </span>
                      )}
                      {(plan.role_name ?? plan.role_code) && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          Role: <span className="font-medium text-[rgb(var(--color-text))]">{plan.role_name ?? plan.role_code}</span>
                        </span>
                      )}
                      {(plan.segment_name ?? plan.segment_code) && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          Segment: <span className="font-medium text-[rgb(var(--color-text))]">{plan.segment_name ?? plan.segment_code}</span>
                        </span>
                      )}
                      {plan.component_count != null && (
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          Components: <span className="font-medium text-[rgb(var(--color-text))]">{plan.component_count}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-[rgb(var(--color-primary))]" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 2 — Choose Population
// Filters: BU, Role, Region, Country, Manager, Segment, Hire Date,
//          Current Plan, Effective Date (required — §E5 contract).
// Population shown as summary count — never raw list (§0 progressive disclosure).
// ---------------------------------------------------------------------------
function Stage2ChoosePopulation({ filters, onChange, fiscalPeriod }) {
  // Lookup data for dropdowns
  const [bus,      setBus]      = useState([]);
  const [roles,    setRoles]    = useState([]);
  const [regions,  setRegions]  = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/business-units`, { credentials: "include" }).then((r) => r.ok ? r.json() : {}),
      fetch(`${API_BASE}/roles`,          { credentials: "include" }).then((r) => r.ok ? r.json() : {}),
      fetch(`${API_BASE}/regions`,        { credentials: "include" }).then((r) => r.ok ? r.json() : {}),
      fetch(`${API_BASE}/segments`,       { credentials: "include" }).then((r) => r.ok ? r.json() : {}),
    ])
      .then(([buData, roleData, regionData, segmentData]) => {
        // Human-readable codes and names only — never expose integer IDs
        setBus(     buData?.business_units  ?? buData?.data  ?? []);
        setRoles(   roleData?.roles          ?? roleData?.data  ?? []);
        setRegions( regionData?.regions      ?? regionData?.data ?? []);
        setSegments(segmentData?.segments    ?? segmentData?.data ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  function set(field, value) {
    onChange({ ...filters, [field]: value });
  }

  const effectiveDateMissing = !filters.effective_date;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Choose Population</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Define the population to assign. Effective date is required. All other filters are optional.
        </p>
      </div>

      {/* Context — fiscal period locked from AssignmentsTab (§6 UX decision) */}
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span className="font-medium text-[rgb(var(--color-text))]">Fiscal Period:</span>{" "}
            {fiscalPeriod?.period_name ?? fiscalPeriod?.period_code ?? "—"}
            {" "}— inherited from Assignments tab. To assign to a different period, close this wizard and change the period first.
          </p>
        </div>
      </div>

      {loading ? <LoadingRow /> : (
        <div className="grid gap-4 sm:grid-cols-2">

          {/* Effective Date — REQUIRED per §E5 */}
          <div className="sm:col-span-2">
            <label className="mb-1.5 flex items-center gap-1 text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Effective Date
              <span className="text-[rgb(var(--color-danger))]">*</span>
            </label>
            <input
              type="date"
              value={filters.effective_date ?? ""}
              onChange={(e) => set("effective_date", e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:border-[rgb(var(--color-primary))] ${
                effectiveDateMissing
                  ? "border-[rgb(var(--color-danger))]/50 bg-[rgb(var(--color-danger))]/5"
                  : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
              }`}
            />
            {effectiveDateMissing && (
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-danger))]">
                Effective date is required for plan assignment.
              </p>
            )}
          </div>

          {/* Business Unit — human-readable bu_code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Business Unit
            </label>
            <select
              value={filters.business_unit_code ?? ""}
              onChange={(e) => set("business_unit_code", e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            >
              <option value="">All Business Units</option>
              {bus.map((bu) => (
                <option key={bu.bu_code} value={bu.bu_code}>
                  {bu.bu_name ?? bu.bu_code}
                </option>
              ))}
            </select>
          </div>

          {/* Role — human-readable role_code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Role
            </label>
            <select
              value={filters.role_code ?? ""}
              onChange={(e) => set("role_code", e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            >
              <option value="">All Roles</option>
              {roles.map((r) => (
                <option key={r.role_code} value={r.role_code}>
                  {r.role_name ?? r.role_code}
                </option>
              ))}
            </select>
          </div>

          {/* Segment — human-readable segment_code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Segment
            </label>
            <select
              value={filters.segment_code ?? ""}
              onChange={(e) => set("segment_code", e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            >
              <option value="">All Segments</option>
              {segments.map((s) => (
                <option key={s.segment_code} value={s.segment_code}>
                  {s.segment_name ?? s.segment_code}
                </option>
              ))}
            </select>
          </div>

          {/* Region — human-readable region_code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Region
            </label>
            <select
              value={filters.region_code ?? ""}
              onChange={(e) => set("region_code", e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r.region_code} value={r.region_code}>
                  {r.region_name ?? r.region_code}
                </option>
              ))}
            </select>
          </div>

          {/* Country — free text ISO code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Country Code
            </label>
            <input
              type="text"
              maxLength={2}
              value={filters.country_code ?? ""}
              onChange={(e) => set("country_code", e.target.value.toUpperCase() || null)}
              placeholder="e.g. US"
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm font-mono text-[rgb(var(--color-text))] placeholder:italic placeholder:font-sans placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            />
          </div>

          {/* Hire Date — from date */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Hired On or After
            </label>
            <input
              type="date"
              value={filters.hire_date_from ?? ""}
              onChange={(e) => set("hire_date_from", e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            />
          </div>

          {/* Current Plan — human-readable plan_code */}
          <div>
            <label className="mb-1.5 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
              Current Plan Code
            </label>
            <input
              type="text"
              value={filters.current_plan_code ?? ""}
              onChange={(e) => set("current_plan_code", e.target.value || null)}
              placeholder="e.g. ENT-AE-Q1FY26"
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm font-mono text-[rgb(var(--color-text))] placeholder:italic placeholder:font-sans placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 3 — Analyze Population
// GET /api/v1/plan-assignments/analyze?fiscal_period_id={id}&plan_id={id}
// 6-category breakdown per §6 spec table.
// fiscal_period_id is internal — stored in state, never displayed.
// ---------------------------------------------------------------------------
function Stage3AnalyzePopulation({ fiscalPeriodId, planCode, planId, onAnalysisComplete }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    if (!fiscalPeriodId || !planId) return;
    setLoading(true);
    setError(null);

    // fiscal_period_id and plan_id are internal integers — passed to API, never shown to user
    fetch(`${API_BASE}/plan-assignments/analyze?fiscal_period_id=${fiscalPeriodId}&plan_id=${planId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); });
        return r.json();
      })
      .then((d) => {
        setData(d);
        onAnalysisComplete?.(d);
      })
      .catch((e) => setError(e.message ?? "Unable to analyze population."))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, planId, onAnalysisComplete]);

  useEffect(() => { load(); }, [load]);

  const summary    = data?.summary    ?? data    ?? null;
  const components = data?.components ?? data?.plan_components ?? [];

  // §6 table — six categories
  const categories = summary ? [
    { label: "Matched",          value: summary.matched          ?? summary.total_matched          ?? "—", desc: "Total positions matching your filters" },
    { label: "Already Assigned", value: summary.already_assigned ?? summary.total_already_assigned ?? "—", desc: "Has active plan for this period" },
    { label: "Inactive",         value: summary.inactive         ?? summary.total_inactive         ?? "—", desc: "Terminated, on leave, or on hold" },
    { label: "Pending Hire",     value: summary.pending_hire     ?? summary.total_pending_hire     ?? "—", desc: "Future start date" },
    { label: "Needs Review",     value: summary.needs_review     ?? summary.total_needs_review     ?? "—", desc: "Multiple conflicts requiring manual resolution" },
    { label: "Ready",            value: summary.ready            ?? summary.total_ready            ?? "—", desc: "Clear for immediate assignment" },
  ] : [];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Analyze Population</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Running population analysis for plan{" "}
          <span className="font-mono font-medium text-[rgb(var(--color-text))]">{planCode}</span>.
          All detection is deterministic — no AI (NN-03).
        </p>
      </div>

      {loading && (
        <div className="space-y-3">
          <LoadingRow />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Analyzing population…</p>
        </div>
      )}
      {error && (
        <div className="space-y-3">
          <ErrorNote message={error} />
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="space-y-4">
          {/* §6 — 6-category breakdown grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {categories.map(({ label, value, desc }) => (
              <div
                key={label}
                className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3 text-center"
                title={desc}
              >
                <p className="text-[20px] font-semibold text-[rgb(var(--color-text))]">{value}</p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
              </div>
            ))}
          </div>

          {/* Per-component breakdown — scroll-bounded per mastercontext */}
          {components.length > 0 && (
            <div className="space-y-2">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                By Plan Component
              </p>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
                <table className="w-full text-tok-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                      <th className="px-4 py-2.5 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Component</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Matched</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Ready</th>
                      <th className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Conflicts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {components.map((comp, idx) => (
                      // Never show plan_component_id — use component_label or index as key
                      <tr
                        key={comp.component_label ?? comp.role_code ?? idx}
                        className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-[rgb(var(--color-text))]">
                            {comp.component_label ?? comp.role_name ?? comp.role_code ?? "—"}
                          </p>
                          {comp.segment_name && (
                            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{comp.segment_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[rgb(var(--color-text))]">
                          {comp.matched ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[rgb(var(--color-success))]">
                          {comp.ready ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          <span className={comp.conflicts > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text-muted))]"}>
                            {comp.conflicts ?? "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* §6 "0 Conflicts Must Explain Itself" — show what was checked */}
          {summary && (summary.ready ?? 0) > 0 && (summary.needs_review ?? 0) === 0 && (summary.already_assigned ?? 0) === 0 && (
            <div className="flex items-center gap-3 rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
              <ShieldCheck className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
              <p className="text-tok-sm text-[rgb(var(--color-success))]">
                Population analysis complete.{" "}
                <span className="font-semibold">{summary.ready ?? 0} position{(summary.ready ?? 0) !== 1 ? "s" : ""} ready for immediate assignment.</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 4 — Resolve Conflicts
// GET /api/v1/plan-assignments/conflicts?fiscal_period_id={id}&plan_id={id}
// All 15 categories per §6 spec. Bulk resolution strategies per category.
// Category 11 (amendment_in_progress): link only — no inline resolution.
// "0 conflicts" must prove the system checked everything.
// ---------------------------------------------------------------------------
function Stage4ResolveConflicts({
  fiscalPeriodId, planId, planCode,
  resolutions, onResolutionChange,
}) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [checked, setChecked] = useState(false);

  const runCheck = useCallback(() => {
    if (!fiscalPeriodId || !planId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setChecked(false);

    // fiscal_period_id and plan_id are internal — passed to API, never displayed
    fetch(`${API_BASE}/plan-assignments/conflicts?fiscal_period_id=${fiscalPeriodId}&plan_id=${planId}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) return r.json().then((b) => { throw new Error(b?.detail?.error?.message ?? `HTTP ${r.status}`); });
        return r.json();
      })
      .then((d) => { setData(d); setChecked(true); })
      .catch((e) => { setError(e.message ?? "Unable to check for conflicts."); setChecked(true); })
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, planId]);

  useEffect(() => { runCheck(); }, [runCheck]);

  const rawConflicts = data?.conflicts ?? data?.conflict_list ?? [];
  const totalErrors  = data?.total_errors   ?? rawConflicts.filter((c) => c.severity === "error").length;
  const totalWarnings= data?.total_warnings ?? rawConflicts.filter((c) => c.severity === "warning").length;

  // Group by category for display
  const grouped = (() => {
    const rawCats = data?.categories ?? data?.conflict_categories ?? [];
    if (rawCats.length > 0) return rawCats;
    const map = {};
    rawConflicts.forEach((c) => {
      const cat = c.conflict_category ?? c.category ?? "other";
      if (!map[cat]) map[cat] = { category: cat, conflicts: [] };
      map[cat].conflicts.push(c);
    });
    return Object.values(map);
  })();

  const hasConflicts = grouped.length > 0;

  function setResolution(category, value) {
    onResolutionChange({ ...resolutions, [category]: value });
  }

  // Find spec entry for a given category key
  function specFor(categoryKey) {
    return CONFLICT_CATEGORIES.find((c) => c.key === categoryKey);
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Resolve Conflicts</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          15-category deterministic conflict detection for plan{" "}
          <span className="font-mono font-medium text-[rgb(var(--color-text))]">{planCode}</span>.
          No AI — all detection is rule-based (NN-03).
        </p>
      </div>

      {loading && (
        <div className="space-y-3">
          <LoadingRow />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Running 15-category conflict detection…</p>
        </div>
      )}
      {error && (
        <div className="space-y-3">
          <ErrorNote message={error} />
          <button
            onClick={runCheck}
            className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* §6: "0 Conflicts Must Explain Itself" — prove the system checked */}
      {checked && !loading && !error && !hasConflicts && (
        <div className="space-y-3">
          <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
              <div>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  No conflicts detected
                </p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">
                  All 15 conflict categories checked — 0 errors, 0 warnings.
                  Population is clear for preview and commit.
                </p>
              </div>
            </div>
          </div>

          {/* Show all 15 categories checked with zero count */}
          <div className="max-h-48 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-tok-xs">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                  <th className="px-4 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Category Checked</th>
                  <th className="px-4 py-2 text-right font-semibold text-[rgb(var(--color-text-muted))]">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {CONFLICT_CATEGORIES.map((cat) => (
                  <tr key={cat.key} className="bg-[rgb(var(--color-surface))]">
                    <td className="px-4 py-2 text-[rgb(var(--color-text-muted))]">{cat.label}</td>
                    <td className="px-4 py-2 text-right">
                      <span className="inline-flex items-center gap-1 text-[rgb(var(--color-success))]">
                        <CheckCircle2 className="h-3 w-3" />
                        0
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conflicts present — grouped, errors first */}
      {checked && !loading && hasConflicts && (
        <div className="space-y-4">
          {/* Summary counts */}
          <div className="flex flex-wrap gap-3">
            {totalErrors > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-3 py-1 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
                <XCircle className="h-3.5 w-3.5" />
                {totalErrors} error{totalErrors !== 1 ? "s" : ""}
              </span>
            )}
            {totalWarnings > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 px-3 py-1 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
                <AlertTriangle className="h-3.5 w-3.5" />
                {totalWarnings} warning{totalWarnings !== 1 ? "s" : ""}
              </span>
            )}
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] self-center">
              Select a bulk resolution strategy for each category.
            </p>
          </div>

          {/* Conflict groups — scroll-bounded */}
          <div className="max-h-80 overflow-y-auto space-y-4 pr-1">
            {grouped.map((group, gIdx) => {
              const catKey     = group.category_key ?? group.category ?? "";
              const spec       = specFor(catKey);
              const groupLabel = spec?.label
                ?? group.category_label
                ?? group.category?.replace(/_/g, " ")
                ?? `Category ${gIdx + 1}`;
              const isAmendment = catKey === "amendment_in_progress";
              const groupConflicts = group.conflicts ?? [];
              const groupErrors   = groupConflicts.filter((c) => c.severity === "error").length;
              const groupWarnings = groupConflicts.filter((c) => c.severity === "warning").length;
              const currentResolution = resolutions[catKey] ?? "";

              return (
                <div
                  key={catKey || gIdx}
                  className={`rounded-xl border px-4 py-3 ${
                    groupErrors > 0
                      ? "border-[rgb(var(--color-danger))]/25 bg-[rgb(var(--color-danger))]/5"
                      : "border-[rgb(var(--color-warning))]/25 bg-[rgb(var(--color-warning))]/5"
                  }`}
                >
                  {/* Category header */}
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {groupErrors > 0
                        ? <XCircle      className="h-4 w-4 shrink-0 text-[rgb(var(--color-danger))]"  />
                        : <AlertTriangle className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                      }
                      <p className={`text-tok-sm font-semibold ${groupErrors > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-warning))]"}`}>
                        {groupLabel}
                      </p>
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                        {groupErrors > 0 ? `${groupErrors} error${groupErrors !== 1 ? "s" : ""}` : `${groupWarnings} warning${groupWarnings !== 1 ? "s" : ""}`}
                      </span>
                    </div>
                  </div>

                  {/* Category 11 — Amendment In Progress: link only, no inline resolution */}
                  {isAmendment && (
                    <div className="rounded-lg border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-surface))] px-3 py-2.5">
                      <p className="text-tok-xs text-[rgb(var(--color-danger))]">
                        A plan amendment is in progress. Assignment is blocked until the amendment is complete.
                        No bulk resolution is available — this must be resolved in the plan before proceeding.
                      </p>
                      <a
                        href="/admin/plans"
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open Plan Detail to complete amendment
                      </a>
                    </div>
                  )}

                  {/* All other categories — bulk resolution dropdown */}
                  {!isAmendment && spec && spec.options.length > 0 && (
                    <div className="mt-2">
                      <label className="mb-1 block text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                        Bulk Resolution for all {groupConflicts.length} affected position{groupConflicts.length !== 1 ? "s" : ""}
                      </label>
                      <select
                        value={currentResolution}
                        onChange={(e) => setResolution(catKey, e.target.value)}
                        className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:border-[rgb(var(--color-primary))] focus:outline-none"
                      >
                        <option value="">— Select resolution —</option>
                        {spec.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                        One resolution applies to all positions in this category.
                      </p>
                    </div>
                  )}

                  {/* Individual conflict descriptions */}
                  {groupConflicts.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      {groupConflicts.slice(0, 3).map((conflict, cIdx) => (
                        <div key={conflict.conflict_id ?? cIdx} className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {conflict.description && `• ${conflict.description}`}
                          {/* §6: operational language — never raw error codes */}
                          {conflict.error_code && !conflict.description && (
                            `• ${resolveErrorMessage(conflict.error_code, conflict.error_code)}`
                          )}
                        </div>
                      ))}
                      {groupConflicts.length > 3 && (
                        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          …and {groupConflicts.length - 3} more in this category.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 5 — Preview Assignment (dry_run=true)
// POST /api/v1/plan-assignments/bulk-import?dry_run=true
// Immutable — no editing. Stable — no regeneration.
// All-or-nothing notice always visible at this stage.
// Commit button only enabled when zero errors.
// ---------------------------------------------------------------------------
function Stage5PreviewAssignment({
  fiscalPeriodId, fiscalPeriod, plan, filters, resolutions,
  onPreviewResult,
}) {
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const runDryRun = useCallback(async () => {
    if (!fiscalPeriodId || !plan) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Build request body — human-readable codes only (§E5 contract)
      // fiscal_period_id carried internally but not shown to user
      const body = {
        plan_code:          plan.plan_code,
        fiscal_period_code: fiscalPeriod?.period_code,
        effective_date:     filters.effective_date,
        business_unit_code: filters.business_unit_code ?? undefined,
        filters: {
          role_code:          filters.role_code          ?? undefined,
          segment_code:       filters.segment_code       ?? undefined,
          region_code:        filters.region_code        ?? undefined,
          country_code:       filters.country_code       ?? undefined,
          hire_date_from:     filters.hire_date_from     ?? undefined,
          current_plan_code:  filters.current_plan_code  ?? undefined,
        },
        conflict_resolutions: resolutions,
        surface: "comp_policies", // UI-05 — audit surface tag
      };

      const res = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=true`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.status === 422) {
        // All-or-nothing: parse error rows, translate codes to operational language
        const rawErrors = data?.detail?.errors ?? data?.errors ?? data?.rejected_rows ?? [];
        const translatedErrors = rawErrors.map((e) => ({
          ...e,
          display_message: resolveErrorMessage(
            data?.detail?.error?.code ?? e.error_code ?? e.code,
            e.message ?? e.detail ?? "Validation error"
          ),
        }));
        const previewResult = { valid: false, errors: translatedErrors, raw: data };
        setResult(previewResult);
        onPreviewResult?.(previewResult);
        return;
      }

      if (!res.ok) {
        const errCode = data?.detail?.error?.code ?? "PREVIEW_ERROR";
        setError(resolveErrorMessage(errCode, `Preview failed — HTTP ${res.status}`));
        onPreviewResult?.({ valid: false, errors: [], raw: data });
        return;
      }

      const previewResult = {
        valid:    true,
        rowCount: data?.row_count ?? data?.matched ?? data?.positions_to_assign ?? 0,
        summary:  data,
        errors:   [],
      };
      setResult(previewResult);
      onPreviewResult?.(previewResult);

    } catch (e) {
      setError(e.message ?? "Preview request failed.");
      onPreviewResult?.({ valid: false, errors: [], raw: null });
    } finally {
      setLoading(false);
    }
  }, [fiscalPeriodId, fiscalPeriod, plan, filters, resolutions, onPreviewResult]);

  useEffect(() => { runDryRun(); }, [runDryRun]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Preview Assignment</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Dry-run validation — no data is written at this stage. This is the safety gate before commit.
        </p>
      </div>

      {/* All-or-nothing notice — always visible, not dismissible (§6 spec) */}
      <AllOrNothingNotice />

      {loading && (
        <div className="space-y-3">
          <LoadingRow />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Validating all rows…</p>
        </div>
      )}

      {error && <ErrorNote message={error} />}

      {result && !loading && (
        <div className="space-y-4">
          {/* Assignment summary — immutable, display only */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-4">
            <p className="mb-3 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Assignment Preview
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan</p>
                <p className="mt-0.5 font-medium text-tok-sm text-[rgb(var(--color-text))]">
                  {plan.plan_name ?? plan.plan_code}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Code</p>
                <p className="mt-0.5 font-mono text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {plan.plan_code}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Fiscal Period</p>
                <p className="mt-0.5 font-medium text-tok-sm text-[rgb(var(--color-text))]">
                  {fiscalPeriod?.period_name ?? fiscalPeriod?.period_code ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective Date</p>
                <p className="mt-0.5 font-mono text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {filters.effective_date ?? "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Valid: all rows pass */}
          {result.valid && (
            <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
              <div>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  All {result.rowCount} row{result.rowCount !== 1 ? "s" : ""} valid. Ready to commit.
                </p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">
                  Dry-run passed — all rows will be written in the commit stage.
                  Proceed to Stage 6 to complete the assignment.
                </p>
              </div>
            </div>
          )}

          {/* Invalid: errors blocking commit */}
          {!result.valid && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-danger))]" />
                <div>
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">
                    {result.errors.length > 0
                      ? `${result.errors.length} error${result.errors.length !== 1 ? "s" : ""} found — commit blocked.`
                      : "Validation failed — commit blocked."}
                  </p>
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-danger))]/80">
                    All errors below must be resolved before the batch can be committed.
                    Return to earlier stages to adjust the population or conflict resolutions.
                  </p>
                </div>
              </div>

              {result.errors.length > 0 && (
                <div className="max-h-56 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <div className="sticky top-0 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2">
                    <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                      Validation errors — {result.errors.length} row{result.errors.length !== 1 ? "s" : ""} affected
                    </p>
                  </div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="border-b border-[rgb(var(--color-border))] px-4 py-2.5">
                      {/* Never show employee UUIDs, position_id, plan_id, or integer FKs */}
                      {e.employee_id && (
                        <p className="font-mono text-tok-xs font-medium text-[rgb(var(--color-text))]">
                          Employee: {e.employee_id}
                        </p>
                      )}
                      {/* §6 Conflict Display Language — operational message, never raw code */}
                      <p className="text-tok-xs text-[rgb(var(--color-danger))]">
                        {e.display_message ?? resolveErrorMessage(e.error_code ?? e.code, e.message ?? "Validation error")}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 6 — Commit
// POST /api/v1/plan-assignments/bulk-import?dry_run=false
// surface='comp_policies' on all audit writes (UI-05).
// Summary only — no editing. Explicit confirmation required.
// Toast on success: "Assignment complete. N positions assigned to [Plan Name]."
// ---------------------------------------------------------------------------
function Stage6Commit({
  fiscalPeriodId, fiscalPeriod, plan, filters, resolutions,
  previewResult, onCommitSuccess, showToast,
}) {
  const [committing, setCommitting] = useState(false);
  const [committed,  setCommitted]  = useState(false);
  const [error,      setError]      = useState(null);
  const [confirmed,  setConfirmed]  = useState(false);

  const rowCount = previewResult?.rowCount ?? 0;

  async function handleCommit() {
    if (!confirmed || committing || committed) return;
    setCommitting(true);
    setError(null);

    try {
      const body = {
        plan_code:          plan.plan_code,
        fiscal_period_code: fiscalPeriod?.period_code,
        effective_date:     filters.effective_date,
        business_unit_code: filters.business_unit_code ?? undefined,
        filters: {
          role_code:           filters.role_code           ?? undefined,
          segment_code:        filters.segment_code        ?? undefined,
          region_code:         filters.region_code         ?? undefined,
          country_code:        filters.country_code        ?? undefined,
          hire_date_from:      filters.hire_date_from      ?? undefined,
          current_plan_code:   filters.current_plan_code   ?? undefined,
        },
        conflict_resolutions: resolutions,
        surface: "comp_policies", // UI-05 — audit surface tag on all writes from this surface
      };

      const res = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=false`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.status === 422) {
        const errCode = data?.detail?.error?.code ?? "COMMIT_ERROR";
        setError(resolveErrorMessage(errCode, "Commit failed — validation error. Please return to Preview and retry."));
        return;
      }

      if (!res.ok) {
        const errCode = data?.detail?.error?.code ?? "COMMIT_ERROR";
        setError(resolveErrorMessage(errCode, `Commit failed — HTTP ${res.status}`));
        return;
      }

      // Success
      const assigned = data?.row_count ?? data?.assigned ?? rowCount;
      setCommitted(true);
      showToast?.(`Assignment complete. ${assigned} position${assigned !== 1 ? "s" : ""} assigned to ${plan.plan_name ?? plan.plan_code}.`);
      onCommitSuccess?.(data);

    } catch (e) {
      setError(e.message ?? "Commit request failed. Please try again.");
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Commit Assignment</h3>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Review the summary below and confirm to write assignments. This action cannot be undone.
        </p>
      </div>

      {/* All-or-nothing notice — always visible at Stage 6 */}
      <AllOrNothingNotice />

      {!committed && (
        <>
          {/* Summary — no editing at Stage 6 */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-4">
            <p className="mb-3 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Assignment Summary
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan</p>
                <p className="mt-0.5 font-medium text-tok-sm text-[rgb(var(--color-text))]">
                  {plan.plan_name ?? plan.plan_code}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan Code</p>
                <p className="mt-0.5 font-mono text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {plan.plan_code}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Fiscal Period</p>
                <p className="mt-0.5 font-medium text-tok-sm text-[rgb(var(--color-text))]">
                  {fiscalPeriod?.period_name ?? fiscalPeriod?.period_code ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective Date</p>
                <p className="mt-0.5 font-mono text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {filters.effective_date ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Positions to Assign</p>
                <p className="mt-0.5 text-[20px] font-semibold text-[rgb(var(--color-text))]">
                  {rowCount}
                </p>
              </div>
              <div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Validation</p>
                <span className="mt-0.5 inline-flex items-center gap-1 text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  <CheckCircle2 className="h-4 w-4" />
                  All rows valid
                </span>
              </div>
            </div>
          </div>

          {/* Explicit confirmation checkbox — required before commit button activates */}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
            />
            <p className="text-tok-sm text-[rgb(var(--color-text))]">
              I confirm that I have reviewed the assignment summary above.
              I understand this will assign{" "}
              <span className="font-semibold">{rowCount} position{rowCount !== 1 ? "s" : ""}</span>{" "}
              to plan{" "}
              <span className="font-mono font-semibold">{plan.plan_code}</span>{" "}
              for fiscal period{" "}
              <span className="font-semibold">{fiscalPeriod?.period_name ?? fiscalPeriod?.period_code ?? "—"}</span>.
              This action generates a governed audit record (NN-36).
            </p>
          </label>

          {error && <ErrorNote message={error} />}

          {/* Commit button — only active when confirmed and dry_run passed */}
          <button
            onClick={handleCommit}
            disabled={!confirmed || committing}
            className="w-full rounded-xl bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:opacity-40 transition"
          >
            {committing ? "Committing assignment…" : `Confirm and Assign ${rowCount} Position${rowCount !== 1 ? "s" : ""}`}
          </button>
        </>
      )}

      {/* Success state */}
      {committed && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
            <div>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                Assignment complete. {rowCount} position{rowCount !== 1 ? "s" : ""} assigned to{" "}
                {plan.plan_name ?? plan.plan_code}.
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-success))]/80">
                Audit record created with surface tag <span className="font-mono">comp_policies</span>.
                Two-actor approval requirement confirmed (NN-36).
              </p>
            </div>
          </div>

          {/* §6: Assignment report placeholder */}
          <div className="flex items-center gap-3 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
            <FileText className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Assignment report generated.{" "}
              <a href="/admin/audit" className="text-[rgb(var(--color-primary))] hover:underline">
                View in Audit &amp; History
              </a>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkAssignmentWizard — root modal component
// Props:
//   fiscalPeriod  { fiscal_period_id (int, internal), period_code, period_name }
//   onClose       () => void
// ---------------------------------------------------------------------------
export default function BulkAssignmentWizard({ fiscalPeriod, onClose }) {
  const [stage,       setStage]       = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [filters,     setFilters]     = useState({ effective_date: "" });
  const [resolutions, setResolutions] = useState({});
  const [previewResult, setPreviewResult] = useState(null);
  const [toastMsg,    setToastMsg]    = useState(null);

  // Internal — fiscal_period_id is an integer used in API calls.
  // It is NEVER displayed to the user. Only period_code / period_name render.
  const fiscalPeriodId = fiscalPeriod?.fiscal_period_id ?? null;

  function showToast(msg) {
    setToastMsg(msg);
  }

  function dismissToast() {
    setToastMsg(null);
  }

  // Stage gating rules
  const canProceedStage1 = !!selectedPlan;
  const canProceedStage2 = !!filters.effective_date;
  // Stages 3 and 4 proceed on data load — Next enabled after analysis/conflict load
  const canProceedStage5 = previewResult?.valid === true;

  function goNext() {
    if (stage < 6) setStage((s) => s + 1);
  }

  function goBack() {
    if (stage > 1) {
      setStage((s) => s - 1);
      // Reset preview when going back from stage 5 or 6 — must re-run dry-run
      if (stage === 5 || stage === 6) setPreviewResult(null);
    }
  }

  function handleCommitSuccess() {
    // Wizard stays open to show success state; onClose available via header X
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Determine Next button state per stage
  function nextEnabled() {
    if (stage === 1) return canProceedStage1;
    if (stage === 2) return canProceedStage2;
    if (stage === 3) return true;  // enabled after analysis loads (no gate needed, stage fires on mount)
    if (stage === 4) return true;  // conflicts may be zero; always proceed-able
    if (stage === 5) return canProceedStage5;
    return false; // stage 6 has its own commit button
  }

  const isLastStage     = stage === 6;
  const showNavigation  = stage < 6;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal — does not close on outside click per §7 pattern */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full max-w-5xl flex-col rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] shadow-2xl"
          style={{ maxHeight: "92vh" }}
        >

          {/* Modal header */}
          <div className="flex shrink-0 items-center justify-between border-b border-[rgb(var(--color-border))] px-8 py-5">
            <div>
              <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
                New Bulk Assignment
              </h2>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Governed population deployment workflow — all-or-nothing commit
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]"
              aria-label="Close wizard"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Stage progress */}
          <div className="shrink-0 overflow-x-auto border-b border-[rgb(var(--color-border))] px-8 py-5">
            <StageProgress current={stage} />
          </div>

          {/* Stage content — scrollable */}
          <div className="flex-1 overflow-y-auto px-8 py-6">

            {stage === 1 && (
              <Stage1ChoosePlan
                selected={selectedPlan}
                onSelect={setSelectedPlan}
              />
            )}

            {stage === 2 && (
              <Stage2ChoosePopulation
                filters={filters}
                onChange={setFilters}
                fiscalPeriod={fiscalPeriod}
              />
            )}

            {stage === 3 && selectedPlan && (
              <Stage3AnalyzePopulation
                fiscalPeriodId={fiscalPeriodId}
                planCode={selectedPlan.plan_code}
                planId={selectedPlan.plan_id} // internal integer — passed to API, never rendered
                onAnalysisComplete={() => {}}
              />
            )}

            {stage === 4 && selectedPlan && (
              <Stage4ResolveConflicts
                fiscalPeriodId={fiscalPeriodId}
                planId={selectedPlan.plan_id} // internal integer — passed to API, never rendered
                planCode={selectedPlan.plan_code}
                resolutions={resolutions}
                onResolutionChange={setResolutions}
              />
            )}

            {stage === 5 && selectedPlan && (
              <Stage5PreviewAssignment
                fiscalPeriodId={fiscalPeriodId}
                fiscalPeriod={fiscalPeriod}
                plan={selectedPlan}
                filters={filters}
                resolutions={resolutions}
                onPreviewResult={setPreviewResult}
              />
            )}

            {stage === 6 && selectedPlan && (
              <Stage6Commit
                fiscalPeriodId={fiscalPeriodId}
                fiscalPeriod={fiscalPeriod}
                plan={selectedPlan}
                filters={filters}
                resolutions={resolutions}
                previewResult={previewResult}
                onCommitSuccess={handleCommitSuccess}
                showToast={showToast}
              />
            )}
          </div>

          {/* Navigation footer — hidden at stage 6 (commit has its own button) */}
          {showNavigation && (
            <div className="shrink-0 flex items-center justify-between border-t border-[rgb(var(--color-border))] px-8 py-5">
              <button
                onClick={goBack}
                disabled={stage === 1}
                className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>

              {/* Stage 2: validation hint */}
              {stage === 2 && !canProceedStage2 && (
                <p className="text-tok-xs text-[rgb(var(--color-danger))]">
                  Effective date required before proceeding.
                </p>
              )}

              {/* Stage 5: commit blocked hint */}
              {stage === 5 && previewResult && !previewResult.valid && (
                <p className="text-tok-xs text-[rgb(var(--color-danger))]">
                  Resolve validation errors before committing.
                </p>
              )}

              <button
                onClick={goNext}
                disabled={!nextEnabled()}
                className="flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:opacity-40 transition"
              >
                {stage === 5 ? "Proceed to Commit" : "Next"}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Stage 6 footer — close only */}
          {isLastStage && (
            <div className="shrink-0 border-t border-[rgb(var(--color-border))] px-8 py-5 text-right">
              <button
                onClick={goBack}
                className="mr-3 rounded-lg border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <span className="flex items-center gap-1.5"><ChevronLeft className="h-4 w-4" />Back to Preview</span>
              </button>
              <button
                onClick={onClose}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface))]"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast — 3000ms auto-dismiss (mastercontext pattern) */}
      {toastMsg && <WizardToast message={toastMsg} onDismiss={dismissToast} />}
    </>
  );
}
